import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import { pick } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { MANAGE_ACCEPT_INVITE_USER_MUTATION } from '../../../constants/mutations/manage';
import useHandleInitialInvitationData from './useHandleInitialInvitationData';
import { broadcastSessionSignal, login, markActivity } from '../../../helpers/session';
import GlobalContext from '../../../providers/GlobalContext';
import * as Yup from 'yup';
import { getUserDeviceInfo } from '../../../helpers/browserInfoHelper';
import { telephoneValidationSchema } from '../../../helpers/validationHelper';

function reducer(currentState, newState) {
  return { ...currentState, ...newState };
}

function useAcceptInvitationForm() {
  const client = useApolloClient();
  const { setGlobalState, setSessionAuthenticated } = useContext(GlobalContext);
  const { initialValues } = useHandleInitialInvitationData();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const [
    {
      success,
      successMessage,
      error,
      errorMessage,
      isEmailOTPSent,
      showEmailOTPSentMessage,
    },
    setState,
  ] = useReducer(reducer, {
    success: false,
    isEmailOTPSent: false,
    showEmailOTPSentMessage: false,
  });
  const [acceptUserInvitation] = useMutation(
    MANAGE_ACCEPT_INVITE_USER_MUTATION,
  );

  const ValidationSchema = useMemo(() => {
    if (isEmailOTPSent) {
      return Yup.object().shape({
        email: Yup.string()
          .email()
          .required('Email is required')
          .max(70, 'Must be at lower than 70 characters'),
        firstName: Yup.string()
          .required('First name is required')
          .max(50, 'Must be at lower than 50 characters'),
        lastName: Yup.string()
          .required('Last name is required')
          .max(50, 'Must be at lower than 50 characters'),
        telephone: telephoneValidationSchema(),
        password: Yup.string().required('Password is required'),
        newPassword: Yup.string().required('Enter your new password'),
        rePassword: Yup.string()
          .required('Enter your password again')
          .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
        emailPassCode: Yup.string().required('Enter otp'),
      });
    } else {
      return Yup.object().shape({
        email: Yup.string()
          .email()
          .required('Email is required')
          .max(70, 'Must be at lower than 70 characters'),
        firstName: Yup.string()
          .required('First name is required')
          .max(50, 'Must be at lower than 50 characters'),
        lastName: Yup.string()
          .required('Last name is required')
          .max(50, 'Must be at lower than 50 characters'),
        telephone: telephoneValidationSchema(),
        password: Yup.string().required('Password is required'),
        newPassword: Yup.string().required('Enter your new password'),
        rePassword: Yup.string()
          .required('Enter your password again')
          .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
      });
    }
  }, [isEmailOTPSent]);

  const loginUserCallback = useCallback(
    async function loginUserCallback(
      setSubmitting,
      email,
      password,
      emailPassCode,
    ) {
      await client.clearStore();
      setState({
        error: false,
        errorMessage: undefined,
      });
      const data = await login({
        email,
        password,
        emailPassCode: emailPassCode || undefined,
        deviceInfo: getUserDeviceInfo(),
        trustDevice: true,
      });
      setState({
        loading: false,
      });
      if (data.success) {
        markActivity();
        broadcastSessionSignal('logged-in');
        setSessionAuthenticated();
        setGlobalState({
          loggedInUser: { ...data.user },
        });
        navigate('/');
      } else if (data.emailPassCodeSent) {
        setState({
          isEmailOTPSent: true,
          showEmailOTPSentMessage: true,
        });
      } else {
        setState({
          errorMessage: data.message ?? 'Failed request',
          error: true,
        });
      }
    },
    [setState, client, navigate, setGlobalState, setSessionAuthenticated],
  );

  const processAcceptInvitation = useCallback(
    async function processAcceptInvitation(
      setSubmitting,
      finalData,
      email,
      password,
      emailPassCode,
    ) {
      setSubmitting(true);
      const result = await acceptUserInvitation({ variables: finalData });
      setSubmitting(false);
      if (result && result.data && result.data.acceptUserInvitation) {
        if (result.data.acceptUserInvitation.success === true) {
          setState({
            error: false,
            errorMessage: undefined,
            success: true,
            successMessage: 'Your account setup is successful',
          });
          await loginUserCallback(
            setSubmitting,
            email,
            password,
            emailPassCode,
          );
        } else {
          setState({
            error: true,
            errorMessage:
              Object.values(result.data)[0] &&
              Object.values(result.data)[0].message
                ? Object.values(result.data)[0].message
                : 'Failed Request',
            success: false,
            successMessage: undefined,
          });
        }
      }
      // eslint-disable-next-line
    },
    [acceptUserInvitation, loginUserCallback],
  );

  const formikOptions = useFormik({
    enableReinitialize: true,
    validationSchema: ValidationSchema,
    initialValues: {
      email: initialValues.email || '',
      firstName: initialValues.firstName || '',
      lastName: initialValues.lastName || '',
      password: initialValues.password || '',
      telephone: initialValues.telephone || '',
      rePassword: '',
      newPassword: '',
      emailPassCode: '',
    },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const finalData = pick(
        values,
        'email',
        'newPassword',
        'firstName',
        'lastName',
        'telephone',
        'emailPassCode',
      );
      const newPasswordStr = finalData.newPassword;
      finalData.password = initialValues.password;
      finalData.newPassword = btoa(finalData.newPassword);
      setState({
        error: false,
        errorMessage: undefined,
        success: false,
        successMessage: undefined,
      });
      try {
        if (isEmailOTPSent && showEmailOTPSentMessage) {
          await loginUserCallback(
            setSubmitting,
            finalData.email,
            newPasswordStr,
            finalData.emailPassCode,
          );
        } else {
          await processAcceptInvitation(
            setSubmitting,
            finalData,
            finalData.email,
            newPasswordStr,
            finalData.emailPassCode,
          );
        }
      } catch (err) {
        console.log(err);
        if (err && err.message) {
          setState({
            error: true,
            errorMessage: err.message,
          });
        }
      }
    },
  });

  useEffect(() => {
    return () => {
      if (timerRef && timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
    };
    // eslint-disable-next-line
  }, []);

  return {
    success,
    successMessage,
    error,
    errorMessage,
    formikOptions,
    isEmailOTPSent,
    showEmailOTPSentMessage,
  };
}

export default useAcceptInvitationForm;
